import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Music, GraduationCap, Camera, Heart, Baby } from 'lucide-react';

const CreatorUseCases = () => {
  const useCases = [
    {
      icon: Users,
      title: "Soccer Mom Sarah",
      subtitle: "Parent & Family Organizer",
      problem: "Phone storage full of game videos, grandparents missing out",
      solution: "Upload game highlights, share privately with family",
      result: "Organized memories, connected grandparents, freed phone space",
      color: "bg-blue-50 border-blue-200",
      iconColor: "text-blue-600"
    },
    {
      icon: Music,
      title: "Piano Teacher Mike",
      subtitle: "Private Music Instructor",
      problem: "Students' parents want to see progress, hard to share videos",
      solution: "Record lessons, share with specific families securely",
      result: "Better parent engagement, more student referrals",
      color: "bg-green-50 border-green-200",
      iconColor: "text-green-600"
    },
    {
      icon: GraduationCap,
      title: "Homeschool Mom Jennifer",
      subtitle: "Home Educator",
      problem: "Extended family wants to see kids' projects and learning",
      solution: "Document learning journey, share safely with family",
      result: "Family stays connected to education, kids feel supported",
      color: "bg-purple-50 border-purple-200",
      iconColor: "text-purple-600"
    },
    {
      icon: Camera,
      title: "Dance Coach Emma",
      subtitle: "Youth Dance Instructor",
      problem: "Teaching private lessons, parents want routine videos",
      solution: "Record technique sessions, share progress videos",
      result: "Parents see improvement, kids practice at home",
      color: "bg-pink-50 border-pink-200",
      iconColor: "text-pink-600"
    },
    {
      icon: Baby,
      title: "Nanny Lisa",
      subtitle: "Child Care Provider",
      problem: "Parents at work want to see what kids are doing",
      solution: "Share daily activity videos privately with parents",
      result: "Parents feel connected, kids' activities documented",
      color: "bg-yellow-50 border-yellow-200",
      iconColor: "text-yellow-600"
    },
    {
      icon: Heart,
      title: "Young Creator Alex",
      subtitle: "Aspiring Athlete",
      problem: "Wants to document basketball progress, share with coaches",
      solution: "Upload practice videos, track skill development",
      result: "Clear progress tracking, college recruitment material",
      color: "bg-red-50 border-red-200",
      iconColor: "text-red-600"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Perfect for Every Family Story
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're a parent, coach, teacher, or young creator - HiveShare Creator makes it easy to share your journey with the people who matter most.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className={`h-full ${useCase.color} border-2 hover:shadow-lg transition-shadow duration-300`}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-full bg-white shadow-sm mr-4`}>
                      <useCase.icon className={`h-6 w-6 ${useCase.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{useCase.title}</h3>
                      <p className="text-sm text-gray-600">{useCase.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-700 text-sm mb-1">😟 The Problem:</h4>
                      <p className="text-gray-700 text-sm">{useCase.problem}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-blue-700 text-sm mb-1">✨ The Solution:</h4>
                      <p className="text-gray-700 text-sm">{useCase.solution}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-green-700 text-sm mb-1">🎯 The Result:</h4>
                      <p className="text-gray-700 text-sm">{useCase.result}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Which Story Sounds Like Yours?
            </h3>
            <p className="text-gray-600 mb-6">
              Every family has special moments worth sharing. HiveShare Creator helps you capture, organize, and share them safely with the people who matter most.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300">
                Start Your Story - $9/month
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-300">
                Learn More
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorUseCases;